import { supabase } from "@/integrations/supabase/client";

export const MAX_TAGS_PER_PUBLICATION = 15;

export interface TagSummary {
  id: string;
  slug: string;
  name: string;
  tag_group?: string | null;
  usage_count?: number | null;
  matched_alias?: string | null;
}

export const TAG_GROUP_LABELS: Record<string, string> = {
  adult_themes: "Adult Themes",
  relationships: "Relationships",
  orientation: "Orientation",
  story_style: "Story Style",
  setting: "Setting",
  tone: "Tone",
  publication_type: "Publication Type",
  other: "Other",
};

/** Strip a leading hash and whitespace so "#BDSM" and "bdsm" behave the same. */
export function normaliseTagQuery(input: string) {
  return input.trim().replace(/^#+/, "").trim();
}

export function tagLabel(tag: { name: string }) {
  return `#${tag.name.replace(/\s+/g, "")}`;
}

export async function searchTags(query: string, limit = 10) {
  const { data, error } = await supabase.rpc("search_tags", {
    _query: normaliseTagQuery(query),
    _limit: limit,
  });
  if (error) throw error;
  return (data ?? []) as TagSummary[];
}

/**
 * Trending is recent, time-decayed engagement (likes, bookmarks, comments,
 * reads and fresh publications) — not lifetime usage.
 */
export async function fetchTrendingTags(limit = 12, days = 14) {
  const { data, error } = await supabase.rpc("trending_tags", { _limit: limit, _days: days });
  if (error) throw error;
  return (data ?? []) as (TagSummary & { score: number })[];
}

export interface TagDetail {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  tag_group: string | null;
  usage_count: number | null;
  status: string;
}

export async function fetchTagBySlug(slug: string): Promise<TagDetail | null> {
  // resolve_tag follows aliases and merged duplicates, so every spelling of a
  // hashtag lands on the same canonical page.
  const { data, error } = await supabase.rpc("resolve_tag", { _slug: normaliseTagQuery(slug) });
  if (error) throw error;
  const row = (data ?? [])[0];
  return row ? (row as unknown as TagDetail) : null;
}


export async function fetchStoryTags(storyId: string) {
  const { data, error } = await supabase
    .from("story_tags")
    .select("tags(id, slug, name, tag_group, usage_count, status, canonical_id)")
    .eq("story_id", storyId);
  if (error) throw error;
  return ((data ?? []) as unknown as {
    tags: (TagSummary & { status?: string | null; canonical_id?: string | null }) | null;
  }[])
    .map((row) => row.tags)
    .filter((tag): tag is TagSummary => Boolean(tag))
    .filter((tag) => {
      // Blocked, archived or merged-away hashtags stop being shown to readers.
      const detail = tag as TagSummary & { status?: string | null; canonical_id?: string | null };
      return (
        (detail.status ?? "active") === "active" && !detail.canonical_id
      );
    });
}

export class TagValidationError extends Error {}

/**
 * The database is the authority on the 15-tag limit, tag approval state and
 * publication ownership; this only translates its errors for the editor.
 */
export async function saveStoryTags(storyId: string, tagIds: string[]) {
  const unique = Array.from(new Set(tagIds));
  const { error } = await supabase.rpc("set_story_tags", {
    _story_id: storyId,
    _tag_ids: unique,
  });
  if (!error) return;
  const message = error.message ?? "Could not save hashtags";
  if (/hashtag|tag/i.test(message)) throw new TagValidationError(message);
  throw error;
}

export async function requestTag(name: string) {
  const { data, error } = await supabase.rpc("request_tag", { _name: normaliseTagQuery(name) });
  if (error) throw error;
  return data as unknown as { id: string; name: string; slug: string; status: string };
}

/* ---------------------------------------------------------------- *
 * Reader preferences — private to the account, never shown publicly *
 * ---------------------------------------------------------------- */

export async function fetchMyTagPreferences(userId: string) {
  const [follows, mutes] = await Promise.all([
    supabase.from("tag_follows").select("tag_id, tags(id, slug, name)").eq("user_id", userId),
    supabase.from("tag_mutes").select("tag_id, tags(id, slug, name)").eq("user_id", userId),
  ]);
  if (follows.error) throw follows.error;
  if (mutes.error) throw mutes.error;
  const flatten = (rows: unknown) =>
    ((rows ?? []) as { tags: TagSummary | null }[])
      .map((row) => row.tags)
      .filter((tag): tag is TagSummary => Boolean(tag));
  return {
    followed: flatten(follows.data),
    muted: flatten(mutes.data),
    followedIds: new Set((follows.data ?? []).map((row) => row.tag_id)),
    mutedIds: new Set((mutes.data ?? []).map((row) => row.tag_id)),
  };
}

export async function setTagFollow(userId: string, tagId: string, follow: boolean) {
  if (follow) {
    const { error } = await supabase
      .from("tag_follows")
      .upsert({ user_id: userId, tag_id: tagId });
    if (error) throw error;
    // Following and muting the same tag is contradictory.
    await supabase.from("tag_mutes").delete().eq("user_id", userId).eq("tag_id", tagId);
  } else {
    const { error } = await supabase
      .from("tag_follows")
      .delete()
      .eq("user_id", userId)
      .eq("tag_id", tagId);
    if (error) throw error;
  }
}

export async function setTagMute(userId: string, tagId: string, mute: boolean) {
  if (mute) {
    const { error } = await supabase.from("tag_mutes").upsert({ user_id: userId, tag_id: tagId });
    if (error) throw error;
    await supabase.from("tag_follows").delete().eq("user_id", userId).eq("tag_id", tagId);
  } else {
    const { error } = await supabase
      .from("tag_mutes")
      .delete()
      .eq("user_id", userId)
      .eq("tag_id", tagId);
    if (error) throw error;
  }
}

/* -------------------------- *
 * Administrator tag registry *
 * -------------------------- */

export type AdminTagFilter = "most_used" | "trending" | "requests" | "recent" | "archived";

export async function fetchAdminTags(filter: AdminTagFilter, search: string) {
  let query = supabase
    .from("tags")
    .select(
      "id, slug, name, description, status, tag_group, usage_count, canonical_id, requested_by, review_note, created_at",
    )
    .limit(100);

  if (filter === "requests") query = query.eq("status", "pending");
  else if (filter === "archived") query = query.in("status", ["archived", "blocked"]);
  else query = query.eq("status", "active");

  if (filter === "most_used" || filter === "trending") {
    query = query.order("usage_count", { ascending: false });
  } else {
    query = query.order("created_at", { ascending: false });
  }

  const clean = normaliseTagQuery(search);
  if (clean) query = query.ilike("name", `%${clean}%`);

  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

export async function fetchTagAliases(tagIds: string[]) {
  if (tagIds.length === 0) return [];
  const { data, error } = await supabase
    .from("tag_aliases")
    .select("id, tag_id, alias, slug")
    .in("tag_id", tagIds);
  if (error) throw error;
  return data ?? [];
}

export interface TagAuditEntry {
  id: string;
  action: string;
  entity_id: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  actor_id: string | null;
  actor_username: string | null;
  actor_display_name: string | null;
  tag_name: string | null;
  tag_slug: string | null;
}

export interface TagAuditFilters {
  tagId?: string | null;
  actions?: string[];
  actorId?: string | null;
  from?: string | null;
  to?: string | null;
  limit?: number;
}

export const TAG_AUDIT_LABELS: Record<string, string> = {
  "tag.created": "created",
  "tag.requested": "requested",
  "tag.approved": "approved",
  "tag.rejected": "rejected",
  "tag.blocked": "blocked",
  "tag.archived": "archived",
  "tag.restored": "restored",
  "tag.merged": "merged",
  "tag.edited": "edited",
  "tag.alias_added": "alias added",
  "tag.alias_removed": "alias removed",
  "tag.status_changed": "status changed",
};

/** Append-only history of every registry change. Administrators and owner only. */
export async function fetchTagAuditLog(filters: TagAuditFilters = {}) {
  const { data, error } = await supabase.rpc("tag_audit_log", {
    ...(filters.tagId ? { _tag_id: filters.tagId } : {}),
    ...(filters.actions && filters.actions.length > 0 ? { _actions: filters.actions } : {}),
    ...(filters.actorId ? { _actor_id: filters.actorId } : {}),
    ...(filters.from ? { _from: filters.from } : {}),
    ...(filters.to ? { _to: filters.to } : {}),
    _limit: filters.limit ?? 100,
  });
  if (error) throw error;
  return (data ?? []) as unknown as TagAuditEntry[];
}

/** Staff who have touched the tag registry, for the audit "actor" filter. */
export async function fetchTagAuditActors() {
  const { data, error } = await supabase.rpc("tag_audit_actors");
  if (error) throw error;
  return (data ?? []) as unknown as {
    actor_id: string;
    username: string | null;
    display_name: string | null;
    entries: number;
  }[];
}

/** Serialises audit rows to CSV (RFC 4180 quoting) for download. */
export function tagAuditToCsv(entries: TagAuditEntry[]) {
  const header = ["timestamp", "action", "tag", "tag_slug", "actor", "actor_username", "details"];
  const escape = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  const rows = entries.map((entry) =>
    [
      new Date(entry.created_at).toISOString(),
      TAG_AUDIT_LABELS[entry.action] ?? entry.action,
      entry.tag_name ?? "",
      entry.tag_slug ?? "",
      entry.actor_display_name ?? "system",
      entry.actor_username ?? "",
      JSON.stringify(entry.metadata ?? {}),
    ]
      .map(escape)
      .join(","),
  );
  return [header.join(","), ...rows].join("\r\n");
}

export interface TagTrendSignal {
  signal: string;
  events: number;
  points: number;
  weight: number;
}

export const TAG_SIGNAL_LABELS: Record<string, string> = {
  reads: "reads",
  likes: "likes",
  bookmarks: "saves",
  comments: "comments",
  publications: "new publications",
};

/** Why a hashtag is trending: recent events, decayed by how long ago they happened. */
export async function fetchTagTrendBreakdown(tagId: string, days = 14) {
  const { data, error } = await supabase.rpc("tag_trend_breakdown", {
    _tag_id: tagId,
    _days: days,
  });
  if (error) throw error;
  return (data ?? []) as unknown as TagTrendSignal[];
}

/**
 * Aliases go through the database so conflicts (already assigned, clashes with a
 * canonical hashtag, merged targets) come back as clear messages.
 */
export async function addTagAlias(tagId: string, alias: string) {
  const { data, error } = await supabase.rpc("add_tag_alias", {
    _tag_id: tagId,
    _alias: alias,
  });
  if (error) throw new TagValidationError(error.message);
  return data;
}

/* -------------------------------- *
 * Publications carrying a hashtag  *
 * -------------------------------- */

export type TagSort = "trending" | "newest" | "most_read" | "most_liked";

export async function fetchTagPublications(
  tagId: string,
  options: {
    sort?: TagSort;
    contentTypeSlug?: string;
    language?: string;
    length?: "short" | "medium" | "long";
    since?: "week" | "month" | "year";
    authorId?: string;
    limit?: number;
  } = {},
) {
  const { STORY_CARD_SELECT } = await import("@/lib/queries");
  // Reuse the existing story_tags embed as an inner join rather than embedding
  // the same relation twice, which PostgREST rejects.
  let select = STORY_CARD_SELECT.replace("story_tags(", "story_tags!inner(");
  if (options.contentTypeSlug) select = select.replace("content_types(", "content_types!inner(");


  let query = supabase
    .from("stories")
    .select(select)
    .eq("status", "published")
    .eq("story_tags.tag_id", tagId)
    .limit(options.limit ?? 24);

  switch (options.sort ?? "trending") {
    case "newest":
      query = query.order("published_at", { ascending: false, nullsFirst: false });
      break;
    case "most_read":
      query = query.order("view_count", { ascending: false });
      break;
    case "most_liked":
      query = query.order("like_count", { ascending: false });
      break;
    default:
      // Trending leans on recent engagement rather than lifetime totals.
      query = query
        .gte("published_at", new Date(Date.now() - 30 * 864e5).toISOString())
        .order("like_count", { ascending: false });
  }

  if (options.contentTypeSlug) query = query.eq("content_types.slug", options.contentTypeSlug);
  if (options.language) query = query.eq("original_language", options.language);
  if (options.authorId) query = query.eq("author_id", options.authorId);
  if (options.length === "short") query = query.lt("word_count", 1500);
  if (options.length === "medium") query = query.gte("word_count", 1500).lt("word_count", 6000);
  if (options.length === "long") query = query.gte("word_count", 6000);
  if (options.since) {
    const days = options.since === "week" ? 7 : options.since === "month" ? 30 : 365;
    query = query.gte("published_at", new Date(Date.now() - days * 864e5).toISOString());
  }

  const { data, error } = await query;
  if (error) throw error;
  const rows = (data ?? []) as unknown as import("@/lib/queries").StoryCardData[];

  // Trending has no fresh rows early on — fall back to newest so the page is never empty.
  if ((options.sort ?? "trending") === "trending" && rows.length === 0) {
    return fetchTagPublications(tagId, { ...options, sort: "newest" });
  }
  return rows;
}

export function storyTags(story: { story_tags?: { tags: TagSummary | null }[] | null }) {
  return (story.story_tags ?? [])
    .map((row) => row.tags)
    .filter((tag): tag is TagSummary => Boolean(tag))
    .filter((tag) => {
      // Only approved, non-merged hashtags are ever shown on a card.
      const detail = tag as TagSummary & { status?: string | null; canonical_id?: string | null };
      return (detail.status ?? "active") === "active" && !detail.canonical_id;
    });
}
